/* =============================================================================
   VIEWS / ADMIN / SETTINGS-DELIVERY-RULES — Liefer-Regeln + Zonen-Editor + Fahrer + Uber-Integration
   =============================================================================
   S66 Refactor: aus settings.js extrahiert (Zeilen 1502-2343, ~842 LOC).

   Block enthält:
     - renderDeliveryRulesCard()         Top-Level Render (Toggle + 3-Spalten-Sub-Grid)
     - renderDeliveryEmptyZonesWarning() Warn-Banner wenn delivery aktiv aber 0 Zonen
     - load*-Funktionen                  Async-Loader für Zonen + Fahrer + Uber-Config
     - update/test/submit/delete/restore Action-Handler für Zonen-CRUD + Fahrer-CRUD + Uber-Toggle

   S66 Strategie-Korrektur (Jonas-Direktive nach S65): Uber Direct fällt für
   den Pilot weg, Uber Eats Marketplace via manager.ubereats.com bleibt
   primärer Pfad. Die Uber-Integration-Card bleibt sichtbar als Foundation,
   Direct-Routing-Hooks sind seit S66 No-Op (siehe core/delivery-routing.js).

   Dependencies aus settings.js werden via window-Wrapper aufgelöst, um
   Circular-Import zu vermeiden (settings.js importiert dieses Modul am
   Anfang, dieses Modul wiederum würde renderSettings/getSettingsSavedHint
   aus settings.js brauchen — main.js setzt diese als window-Side-Effect).
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { safeInvoke } from '../../core/edge-functions.js';

// -----------------------------------------------------------------------------
// Cross-Module Helpers — lokale Wrapper für settings.js-internas (Anti-Circular)
// -----------------------------------------------------------------------------
function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}
function _renderSettings() {
  if (typeof window !== 'undefined' && typeof window.renderSettings === 'function') {
    window.renderSettings();
  }
}
function _getSettingsSavedHint() {
  if (typeof window !== 'undefined' && typeof window.getSettingsSavedHint === 'function') {
    return window.getSettingsSavedHint();
  }
  return 'Gespeichert.';
}
// -----------------------------------------------------------------------------
// Section: Lieferungs-Regeln-Card — Toggles + Zonen-Editor (S60 Block A + B).
// Wird als dritte Karte im „Regeln"-Sub-Tab gerendert (3-Pfad-Konsistenz mit
// Reservierungs-Regeln + Bestell-Regeln). KEIN eigener Sub-Tab.
// -----------------------------------------------------------------------------
export function renderDeliveryRulesCard() {
  const state = getState();
  const d = (state.settings && state.settings.delivery) || {};
  const prepMin = Number(d.default_prep_minutes || 30);
  // S143 / Ticket 1 — USt-Satz der Liefergebühr (steuerlich strittig → konfigurierbar,
  // Jonas-Entscheid 2026-06-23). Default 19 % (eigenständige Lieferleistung). Greift
  // serverseitig in create_customer_order/create_admin_order (phase_109): die Gebühr
  // aus delivery_address.fee_cents wird mit diesem Satz in total/Steuer gefaltet.
  const feeTaxRate = (Number(d.fee_tax_rate) === 7) ? 7 : 19;
  // Mode-Ableitung: Single-Source ist d.mode ∈ {'internal_only', 'internal_with_uber_fallback', 'uber_only'}.
  // Fallback aus Legacy-Booleans wenn mode nicht gesetzt.
  const mode = d.mode || (
      d.uber_marketplace_enabled ? 'uber_only'
    : d.uber_direct_fallback_enabled ? 'internal_with_uber_fallback'
    : 'internal_only'
  );
  const uberConn = d.uber_connection || { configured: false };
  const uberWarn = !uberConn.configured ? `
    <div class="setting-hint" style="background:var(--warn-soft);border-left:3px solid var(--accent);padding:8px 10px;margin-top:6px;color:var(--accent-dark);">
      🔌 <strong>Uber-Verbindung: noch nicht konfiguriert</strong> — Setup-Flow folgt in S61
      (OAuth + Direct-API + Eats-Webhook). Modus kann jetzt gewählt werden, Routing greift sobald
      die API-Verbindung steht.
    </div>` : '';
  return `
    <!-- S63 Hotfix — Uber-Integration als eigene Top-Level-Card vor dem
         3-Spalten-Sub-Grid. Eigene Reihe, volle Breite — die Marketplace-
         Anbindung beeinflusst alle drei Liefer-Karten unten. -->
    <div class="card" id="uber-integration-card" style="margin-bottom: 16px;">
      <h2>🛵 Uber-Integration <span class="badge-soft" style="font-size:.7rem;">Sandbox/Production</span></h2>
      <p style="color: var(--ink-soft); font-size: .92rem;">
        Eingehende Uber-Eats-Bestellungen und Uber-Direct-Lieferungen.
        Trage Client-ID + Secret aus dem
        <a href="https://developer.uber.com/" target="_blank" rel="noopener">Uber-Developer-Dashboard</a>
        ein und teste die Verbindung. Webhook-URL für das Uber-Hub:
        <code style="user-select:all;font-size:.82rem;display:block;margin-top:6px;padding:6px 10px;background:var(--bg-soft,#fafaf7);border:1px solid var(--border);border-radius:6px;word-break:break-all;">https://taemjqkjswnohntjtimf.supabase.co/functions/v1/uber-eats-order-webhook</code>
      </p>
      <div id="uber-config-status" style="margin-top:8px;"></div>

      <div style="display:grid;grid-template-columns: 1fr 1fr;gap:14px;margin-top:14px;">
        <div class="field">
          <label>Environment</label>
          <select id="uber-env-select" onchange="updateUberConfigField('environment', this.value)">
            <option value="sandbox">🧪 Sandbox (Test, keine echten Lieferungen)</option>
            <option value="production">🚀 Production (echte Lieferungen + echte Kosten)</option>
          </select>
        </div>
        <div class="field">
          <label>Client ID</label>
          <input type="text" id="uber-client-id" placeholder="Aus Uber-Developer-Dashboard"
                 onchange="updateUberConfigField('client_id', this.value)" style="width:100%;font-family:ui-monospace,monospace;">
        </div>
        <div class="field">
          <label>Client Secret</label>
          <input type="password" id="uber-client-secret" placeholder="••••••••••••"
                 onchange="updateUberConfigField('client_secret', this.value)" style="width:100%;font-family:ui-monospace,monospace;">
        </div>
        <div class="field">
          <label>Webhook Signing Secret</label>
          <input type="password" id="uber-webhook-secret" placeholder="••••••••••••"
                 onchange="updateUberConfigField('webhook_signing_secret', this.value)" style="width:100%;font-family:ui-monospace,monospace;">
        </div>
        <div class="field" style="grid-column: 1 / -1;">
          <label>Customer ID <span style="font-weight:400;color:var(--ink-soft);font-size:.82rem;">(Pflicht für Uber Direct — aus Uber-Dashboard → Developer → API Keys)</span></label>
          <input type="text" id="uber-customer-id" placeholder="z.B. cust_xxxx oder UUID"
                 onchange="updateUberConfigField('customer_id', this.value)" style="width:100%;font-family:ui-monospace,monospace;">
        </div>
      </div>
      <p class="setting-hint" style="margin-top:8px;">
        Werte werden in <code>internal_config</code> gespeichert (owner/manager-only). HMAC-SHA256-Key kommt aus dem Uber-Hub bei der Webhook-Registrierung.
        Customer ID wird im API-Pfad <code>/v1/customers/{id}/deliveries</code> verwendet — ohne sie schlägt jeder Direct-Call mit 503 fehl.
      </p>

      <div style="display:flex;gap:8px;margin-top:16px;flex-wrap:wrap;">
        <button class="btn btn-primary" onclick="testUberConnection()">🔌 Verbindung testen</button>
        <button class="btn btn-ghost" onclick="loadUberConfigStatus()">↻ Status aktualisieren</button>
      </div>
    </div>

    <!-- S62 Hotfix-2 — Liefer-Sub-Sektion: 3 Cards (Regeln + Zonen + Fahrer)
         bekommen volle Breite im settings-grid und legen sich intern in einem
         eigenen 3-Spalten-Sub-Grid an. Vermeidet den 280px-Squeeze, in dem
         Telefon + E-Mail in der Fahrer-Liste abgeschnitten wurden. -->
    <div class="settings-delivery-group">
    <div class="card">
      <h2>Lieferungs-Regeln <span class="badge-soft" style="font-size:.7rem;">Delivery</span></h2>
      <p style="color: var(--ink-soft); font-size: .92rem; margin-bottom: 16px;">
        Wähle den Liefer-Modus deines Restaurants. Diese Entscheidung steuert,
        wer Bestellungen annimmt und wer liefert.
      </p>

      <!-- S63 — Empty-State-Warner: wird async via loadDeliveryZonesList() befüllt,
           wenn Mode internal_* aber 0 aktive Zonen → roter Banner mit CTA.
           Customer-Form check_delivery_zone würde sonst alles abweisen → Revenue-Leak. -->
      <div id="delivery-empty-zones-warning" data-mode="${mode}"></div>

      <div class="setting-row">
        <label class="radio-label" style="display:flex;gap:10px;align-items:flex-start;padding:8px 0;">
          <input type="radio" name="delivery-mode" value="internal_only"
                 ${mode === 'internal_only' ? 'checked' : ''}
                 onchange="updateDeliveryMode('internal_only')"
                 style="margin-top:3px;">
          <span>
            <strong>🚲 Nur eigene Fahrer</strong>
            <div class="setting-hint" style="margin-top:2px;">Volle Kontrolle, niedrigste Kosten. Bestellungen ausschließlich über die eigene Website.</div>
          </span>
        </label>
      </div>

      <div class="setting-row">
        <label class="radio-label" style="display:flex;gap:10px;align-items:flex-start;padding:8px 0;">
          <input type="radio" name="delivery-mode" value="internal_with_uber_fallback"
                 ${mode === 'internal_with_uber_fallback' ? 'checked' : ''}
                 onchange="updateDeliveryMode('internal_with_uber_fallback')"
                 style="margin-top:3px;">
          <span>
            <strong>🤝 Eigene Fahrer + Uber Direct Fallback</strong>
            <div class="setting-hint" style="margin-top:2px;">Eigene Fahrer als Default, Uber Direct springt bei Auslastung als Backup ein. Bestellungen über die eigene Website.</div>
            ${mode === 'internal_with_uber_fallback' ? uberWarn : ''}
          </span>
        </label>
      </div>

      <div class="setting-row">
        <label class="radio-label" style="display:flex;gap:10px;align-items:flex-start;padding:8px 0;">
          <input type="radio" name="delivery-mode" value="uber_only"
                 ${mode === 'uber_only' ? 'checked' : ''}
                 onchange="updateDeliveryMode('uber_only')"
                 style="margin-top:3px;">
          <span>
            <strong>🛵 Nur Uber Eats Marketplace</strong>
            <div class="setting-hint" style="margin-top:2px;">Bestellungen kommen über Uber Eats rein, Uber-Driver liefert (~30% Provision). Eigene Website-Bestellungen deaktiviert.</div>
            ${mode === 'uber_only' ? uberWarn : ''}
          </span>
        </label>
      </div>

      <div class="setting-row" style="margin-top:16px;border-top:1px dashed var(--border);padding-top:12px;">
        <label>Standard-Zubereitungszeit (Minuten)</label>
        <input type="number" min="5" max="120" step="5" value="${prepMin}"
               onchange="updateDeliveryToggle('default_prep_minutes', Number(this.value) || 30)"
               style="width:80px;">
        <p class="setting-hint">Zeit von Bestellannahme bis fahrbereit. Wird Customer-Form als Liefer-ETA-Basis genutzt.</p>
      </div>

      <div class="setting-row" style="margin-top:14px;">
        <label>USt-Satz Liefergebühr</label>
        <select onchange="updateDeliveryToggle('fee_tax_rate', Number(this.value))" style="width:auto;min-width:200px;">
          <option value="19" ${feeTaxRate === 19 ? 'selected' : ''}>19 % (eigenständige Lieferleistung)</option>
          <option value="7" ${feeTaxRate === 7 ? 'selected' : ''}>7 % (Nebenleistung Speisen)</option>
        </select>
        <p class="setting-hint">
          Steuersatz, mit dem die Liefergebühr in Bestelltotal, Steueraufschlüsselung und TSE-Beleg
          eingerechnet wird. Steuerlich strittig — im Zweifel mit dem Steuerberater abstimmen.
          Default 19 %. Wirkt auf neue Bestellungen.
        </p>
      </div>
    </div>

    <div class="card" style="margin-top: 16px;">
      <h2>📍 Liefer-Zonen</h2>
      <p style="color: var(--ink-soft); font-size: .92rem;">
        Postleitzahl-basierte Zonen mit Liefergebühr und Mindestbestellwert.
        Beim Bestellen prüft die Customer-Form die PLZ gegen diese Liste.
      </p>
      <div id="delivery-zones-list" style="margin-top: 12px;">Lade Zonen…</div>
      <div style="margin-top: 16px; border-top: 1px dashed var(--border); padding-top: 12px;">
        <strong>Neue Zone anlegen</strong>
        <div class="field" style="margin-top: 10px;">
          <label>Name</label>
          <input type="text" id="zone-name" placeholder="z.B. Innenstadt" style="width:100%;">
        </div>
        <div class="field" style="margin-top: 10px;">
          <label>PLZ (durch Komma getrennt) <span style="font-size:.78rem;color:var(--ink-soft);font-weight:normal;">— Prefix möglich z.B. <code>8033*</code></span></label>
          <input type="text" id="zone-plz" placeholder="80331, 80333, 8033* (für 80330–80339)" style="width:100%;">
        </div>
        <div class="field" style="margin-top: 10px;">
          <label>Liefergebühr (€)</label>
          <input type="number" id="zone-fee" min="0" step="0.10" value="3.90" style="width:100%;">
        </div>
        <div class="field" style="margin-top: 10px;">
          <label>Mindestbestellwert (€)</label>
          <input type="number" id="zone-min" min="0" step="1" value="15" style="width:100%;">
        </div>
        <button class="btn btn-primary" onclick="submitNewDeliveryZone()" style="margin-top: 14px; width:100%;">
          + Zone hinzufügen
        </button>
      </div>

      <details style="margin-top: 16px; border-top: 1px dashed var(--border); padding-top: 12px;">
        <summary style="cursor: pointer; font-weight: 500; color: var(--ink-soft, #666); font-size: .9rem;">
          🗑️ Gelöschte Zonen
        </summary>
        <div id="delivery-zones-deleted-list" style="margin-top: 10px;">
          <p style="color: var(--ink-soft); font-style: italic; font-size: .85rem;">Lade …</p>
        </div>
      </details>
    </div>

    <!-- S209/phase_156 — Fahrer sind jetzt Team-User mit Rolle „Fahrer"
         (Operator-Layer, PIN-Login). Die eigenständige Fahrer-Stammdaten-Pflege
         ist ersatzlos hierher verschoben → nur noch ein Verweis, damit niemand
         die alte Stelle sucht. -->
    <div class="card" style="margin-top: 16px;">
      <h2>🚴 Fahrer</h2>
      <p style="color: var(--ink-soft); font-size: .92rem;">
        Fahrer werden ab sofort unter <strong>System → Team</strong> mit der
        Rolle <strong>„Fahrer"</strong> angelegt und verwaltet (mit PIN + Telefon).
        Sie melden sich am mobilen <strong>Fahrer-Terminal</strong> an, sehen dort
        ihre eigenen Lieferungen und schalten „Unterwegs → Zugestellt". Im
        Liefer-Cockpit erscheinen sie automatisch im „Fahrer zuweisen"-Dropdown.
      </p>
      <a href="#" class="btn btn-secondary btn-sm" style="margin-top: 6px;"
         onclick="event.preventDefault(); if(window.setSettingsSection) window.setSettingsSection('team');">
        → Zum Team
      </a>
    </div>
    </div><!-- /.settings-delivery-group -->

  `;
}

/** S60 Block B — Async-Loader für Liefer-Zonen-Liste.
 *  Wird nach jedem _renderSettings()-Aufruf in setSettingsSection('rules')
 *  via setTimeout(0) gerufen (innerHTML-Injection führt inline-Scripts nicht aus). */
export async function loadDeliveryZonesList() {
  const el = document.getElementById('delivery-zones-list');
  if (!el) return; // Sub-Tab nicht aktiv → kein Render nötig
  if (!window.App || !window.App.storage || !window.App.storage.deliveryZones) {
    el.innerHTML = '<p style="color: var(--ink-soft); font-style: italic; font-size: .9rem;">Adapter nicht initialisiert.</p>';
    return;
  }
  try {
    const zones = await window.App.storage.deliveryZones.list();
    // S63 — Empty-State-Warner: bei 0 aktiven Zonen + internal_* Mode prominent warnen.
    renderDeliveryEmptyZonesWarning(Array.isArray(zones) ? zones.length : 0);
    if (!zones || zones.length === 0) {
      el.innerHTML = '<p style="color: var(--ink-soft); font-style: italic; font-size: .9rem;">Noch keine Zonen angelegt.</p>';
    } else {
      el.innerHTML = zones.map(z => `
        <div style="background: var(--bg-soft, #fafaf7); border: 1px solid var(--border); border-radius: 8px; padding: 10px 12px; margin-bottom: 8px;">
          <div style="display:flex; align-items:flex-start; justify-content:space-between; gap:8px;">
            <strong style="font-size: .98rem;">${htmlEscape(z.name || '—')}</strong>
            <button class="btn btn-ghost btn-sm" onclick="deleteDeliveryZone('${htmlEscape(z.id)}')" title="Zone löschen" style="padding:2px 8px;">🗑️</button>
          </div>
          <div style="color: var(--ink-soft, #666); font-family: ui-monospace, monospace; font-size: .82rem; margin: 4px 0 6px;">
            ${(z.plz_list || []).map(htmlEscape).join(' · ') || '<em>keine PLZ</em>'}
          </div>
          <div style="display:flex; gap:14px; font-size: .85rem; color: var(--ink-soft, #666);">
            <span><strong style="color: var(--ink, #222);">${((z.fee_cents || 0) / 100).toFixed(2).replace('.', ',')} €</strong> Liefergebühr</span>
            <span>Min. <strong style="color: var(--ink, #222);">${((z.min_order_cents || 0) / 100).toFixed(2).replace('.', ',')} €</strong></span>
          </div>
        </div>
      `).join('');
    }
  } catch (err) {
    console.error('[settings] loadDeliveryZonesList:', err);
    el.innerHTML = '<p style="color: var(--danger, #c00);">Fehler beim Laden der Zonen: ' + htmlEscape(err.message || String(err)) + '</p>';
  }
  // S63 Hotfix — Restore-View IMMER laden, auch wenn aktive Zonen-Liste leer ist
  // (vorher early-return innerhalb try → Restore blieb auf "Lade..." hängen wenn 0 aktive Zonen).
  loadDeliveryZonesDeleted();
}

/** S60 Block B2 — Restore-View für soft-deletete Liefer-Zonen.
 *  Pattern analog menu_items/tables (S58) — Stammdaten-Restore lebt im Editor,
 *  nicht im Archiv (Memory feedback_gg_restore_view_placement). */
export async function loadDeliveryZonesDeleted() {
  const el = document.getElementById('delivery-zones-deleted-list');
  if (!el) return;
  if (!window.App || !window.App.storage || !window.App.storage.deliveryZones
      || typeof window.App.storage.deliveryZones.listDeleted !== 'function') {
    el.innerHTML = '<p style="color: var(--ink-soft); font-style: italic; font-size: .85rem;">Restore-Adapter nicht verfügbar.</p>';
    return;
  }
  try {
    const zones = await window.App.storage.deliveryZones.listDeleted();
    if (!zones || zones.length === 0) {
      el.innerHTML = '<p style="color: var(--ink-soft); font-style: italic; font-size: .85rem;">Keine gelöschten Zonen.</p>';
      return;
    }
    el.innerHTML = zones.map(z => {
      const delAt = z.deleted_at ? new Date(z.deleted_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—';
      const delBy = z.deleted_by || '—';
      return `
        <div style="background: var(--bg-soft, #fafaf7); border: 1px solid var(--border); border-radius: 8px; padding: 10px 12px; margin-bottom: 8px; opacity: 0.85;">
          <div style="display:flex; align-items:flex-start; justify-content:space-between; gap:8px;">
            <strong style="font-size: .95rem;">${htmlEscape(z.name || '—')}</strong>
            <button class="btn btn-ghost btn-sm" onclick="restoreDeliveryZone('${htmlEscape(z.id)}')" title="Zone wiederherstellen" style="padding:2px 8px;">↺ Restore</button>
          </div>
          <div style="color: var(--ink-soft, #666); font-family: ui-monospace, monospace; font-size: .8rem; margin: 4px 0 4px;">
            ${(z.plz_list || []).map(htmlEscape).join(' · ') || '<em>keine PLZ</em>'}
          </div>
          <div style="display:flex; gap:14px; font-size: .8rem; color: var(--ink-soft, #666);">
            <span>${((z.fee_cents || 0) / 100).toFixed(2).replace('.', ',')} € / Min. ${((z.min_order_cents || 0) / 100).toFixed(2).replace('.', ',')} €</span>
          </div>
          <div style="margin-top:6px; font-size: .72rem; color: var(--ink-soft, #888); font-style: italic;">
            Gelöscht ${htmlEscape(delAt)} · von ${htmlEscape(delBy)}${z.deleted_reason ? ' · ' + htmlEscape(z.deleted_reason) : ''}
          </div>
        </div>
      `;
    }).join('');
  } catch (err) {
    console.error('[settings] loadDeliveryZonesDeleted:', err);
    el.innerHTML = '<p style="color: var(--danger, #c00); font-size: .85rem;">Fehler: ' + htmlEscape(err.message || String(err)) + '</p>';
  }
}

/** S63 — Empty-State-Warner für Liefer-Zonen.
 *  Wenn der Mode internal_* ist (= eigene Website nimmt Lieferungen entgegen)
 *  aber 0 aktive Zonen existieren, würde Customer-Form check_delivery_zone()
 *  jede Lieferanfrage abweisen → Revenue-Leak ohne sichtbare Ursache.
 *  Wir zeigen einen prominenten roten Banner mit klarem CTA. */
export function renderDeliveryEmptyZonesWarning(zoneCount) {
  const el = document.getElementById('delivery-empty-zones-warning');
  if (!el) return;
  const mode = el.dataset.mode || 'internal_only';
  const needsZones = (mode === 'internal_only' || mode === 'internal_with_uber_fallback');
  if (!needsZones || zoneCount > 0) {
    el.innerHTML = '';
    return;
  }
  el.innerHTML = `
    <div style="background:var(--danger-soft);border-left:4px solid var(--danger);border-radius:8px;padding:12px 14px;margin-bottom:14px;color:var(--danger);">
      <div style="display:flex;gap:10px;align-items:flex-start;">
        <div style="font-size:1.2rem;line-height:1;">⚠️</div>
        <div style="flex:1;">
          <strong style="display:block;margin-bottom:4px;">Keine Liefer-Zonen aktiv — Lieferungen werden blockiert</strong>
          <div style="font-size:.9rem;line-height:1.4;">
            Modus <code>${htmlEscape(mode)}</code> erwartet eigene Liefer-Zonen, aber es ist keine PLZ-Zone aktiv.
            Die Customer-Form weist jede Lieferanfrage ab. Lege unten mindestens eine Zone an
            oder wechsle in den Modus „🛵 Nur Uber Eats Marketplace".
          </div>
        </div>
      </div>
    </div>
  `;
}

// -----------------------------------------------------------------------------
// S63 Block U.6 — Uber-Integration Settings-UI
// -----------------------------------------------------------------------------

/** Speichert ein einzelnes Uber-Config-Feld via set_uber_config-RPC.
 *  Felder werden NUR übermittelt wenn sie aktiv geändert wurden (onchange). */
export async function updateUberConfigField(field, value) {
  if (!window.App || !window.App.supabase) {
    window.showToast && window.showToast('Supabase-Client nicht initialisiert.', 'error');
    return;
  }
  if (value === '') return; // Leer-Eingabe ignorieren
  const supa = window.App.supabase;
  // S65 phase_70: customer_id ergänzt
  const allowed = ['client_id', 'client_secret', 'customer_id', 'webhook_signing_secret', 'environment'];
  if (!allowed.includes(field)) return;
  const params = { p_client_id: null, p_client_secret: null, p_customer_id: null, p_webhook_signing_secret: null, p_environment: null };
  if (field === 'client_id')              params.p_client_id = value;
  if (field === 'client_secret')          params.p_client_secret = value;
  if (field === 'customer_id')            params.p_customer_id = value;
  if (field === 'webhook_signing_secret') params.p_webhook_signing_secret = value;
  if (field === 'environment')            params.p_environment = value;
  try {
    const { error } = await supa.rpc('set_uber_config', params);
    if (error) throw error;
    window.showToast && window.showToast('Uber-Config gespeichert.', 'success');
    loadUberConfigStatus();
  } catch (e) {
    console.error('[updateUberConfigField]', e);
    window.showToast && window.showToast('Fehler: ' + (e.message || e), 'error');
  }
}

/** Lädt den Konfigurations-Status (welche Secrets gesetzt, welches Environment,
 *  Token-Cache-Stand) und zeigt ihn in #uber-config-status an. */
export async function loadUberConfigStatus() {
  const el = document.getElementById('uber-config-status');
  if (!el) return;
  if (!window.App || !window.App.supabase) {
    el.innerHTML = '<p style="color:var(--ink-soft);font-style:italic;font-size:.85rem;">Supabase-Client nicht initialisiert.</p>';
    return;
  }
  try {
    const { data, error } = await window.App.supabase.rpc('get_uber_config');
    if (error) throw error;
    const cfg = Array.isArray(data) ? data[0] : data;
    const has = (v) => v && String(v).length > 0;
    const envSel = document.getElementById('uber-env-select');
    if (envSel && cfg.environment) envSel.value = cfg.environment;
    // S65 phase_70: Customer-ID-Feld vorausfüllen wenn schon gesetzt
    // (zeigt UUID/cust_-Wert, weil customer_id kein Geheimnis ist)
    const custIdInput = document.getElementById('uber-customer-id');
    if (custIdInput && cfg.customer_id) custIdInput.value = cfg.customer_id;
    const tokenExp = cfg.oauth_token_expires_at;
    const tokenStr = tokenExp
      ? `Token gecached, läuft ${new Date(tokenExp).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}`
      : 'kein Token gecached';
    // S65 phase_70: customer_id ist Pflicht für Direct-Calls, daher in der Vollständigkeits-Prüfung
    const allConfigured = has(cfg.client_id) && has(cfg.client_secret) && has(cfg.customer_id) && has(cfg.webhook_signing_secret);
    el.innerHTML = `
      <div style="background:${allConfigured ? 'var(--green-soft)' : 'var(--danger-soft)'};border-left:4px solid ${allConfigured ? 'var(--green)' : 'var(--danger)'};border-radius:6px;padding:10px 12px;font-size:.88rem;">
        <strong>${allConfigured ? '✅ Konfiguration vollständig' : '⚠️ Konfiguration unvollständig'}</strong>
        <ul style="margin:6px 0 0 18px;padding:0;line-height:1.6;">
          <li>Environment: <strong>${htmlEscape(cfg.environment || '—')}</strong></li>
          <li>Client ID: ${has(cfg.client_id) ? '✅ gesetzt' : '❌ fehlt'}</li>
          <li>Client Secret: ${has(cfg.client_secret) ? '✅ gesetzt' : '❌ fehlt'}</li>
          <li>Customer ID: ${has(cfg.customer_id) ? '✅ <code style="font-size:.78rem;">' + htmlEscape(String(cfg.customer_id).slice(0, 8)) + '…</code>' : '❌ fehlt'}</li>
          <li>Webhook Signing Secret: ${has(cfg.webhook_signing_secret) ? '✅ gesetzt' : '❌ fehlt'}</li>
          <li>OAuth-Token: ${tokenStr}</li>
        </ul>
      </div>
    `;
  } catch (e) {
    el.innerHTML = '<p style="color:var(--danger,#c00);font-size:.85rem;">Status nicht lesbar: ' + htmlEscape(e.message || String(e)) + '</p>';
  }
}

/** Triggert uber-auth-Edge-Function und zeigt das Ergebnis als Toast.
 *  S63 Hotfix — Toast-Detail extrahiert aus dem Edge-Function-Response-Body,
 *  damit der Operator den echten Grund sieht (z.B. „uber_not_configured")
 *  statt nur „non-2xx status code". Bei FunctionsHttpError ist der Body in
 *  error.context.response abrufbar. */
export async function testUberConnection() {
  if (!window.App || !window.App.supabase) {
    window.showToast && window.showToast('Supabase-Client nicht initialisiert.', 'error');
    return;
  }
  window.showToast && window.showToast('Verbindung wird getestet …', 'info', 2000);
  try {
    const { data, error } = await safeInvoke('uber-auth', { force: false });
    if (error) {
      // Versuche den Body aus dem FunctionsHttpError zu lesen
      let detail = error.message || String(error);
      let bodyParsed = null;
      try {
        if (error.context && typeof error.context.json === 'function') {
          bodyParsed = await error.context.json();
        } else if (error.context && error.context.response && typeof error.context.response.text === 'function') {
          const t = await error.context.response.text();
          try { bodyParsed = JSON.parse(t); } catch { detail = t || detail; }
        }
      } catch { /* ignore */ }
      if (bodyParsed && bodyParsed.error) {
        detail = bodyParsed.error + (bodyParsed.detail ? ' — ' + bodyParsed.detail : '');
      }
      window.showToast && window.showToast('❌ ' + detail, 'error', 7000);
      loadUberConfigStatus();
      return;
    }
    if (data && data.cached) {
      window.showToast && window.showToast('✅ Token aus Cache, läuft ' + new Date(data.expires_at).toLocaleString('de-DE'), 'success');
    } else if (data && data.expires_at) {
      window.showToast && window.showToast('✅ Neuer Token bezogen, läuft ' + new Date(data.expires_at).toLocaleString('de-DE'), 'success');
    } else {
      window.showToast && window.showToast('Antwort unerwartet: ' + JSON.stringify(data), 'error');
    }
    loadUberConfigStatus();
  } catch (e) {
    const detail = (e && e.message) || String(e);
    window.showToast && window.showToast('❌ Verbindung fehlgeschlagen: ' + detail, 'error', 6000);
  }
}

// -----------------------------------------------------------------------------
// S209/phase_156 — Fahrer-CRUD komplett entfernt. Fahrer sind jetzt Team-User
// mit Rolle „Fahrer" (Operator-Layer) und werden unter System → Team gepflegt.
// Die frühere drivers-Tabelle + ihre CRUD-RPCs (upsert_driver/soft_delete_driver)
// sind gedroppt; der Liefer-Cockpit-Dropdown liest über list_assignable_drivers.
// -----------------------------------------------------------------------------

/** S60 Block B2 — Liefer-Zone aus Soft-Delete wiederherstellen. */
export async function restoreDeliveryZone(id) {
  if (!id) return;
  if (typeof window !== 'undefined' && window.App && window.App.auth
      && typeof window.App.auth.canEdit === 'function'
      && !window.App.auth.canEdit('settings.rules')) {
    return window.showToast && window.showToast('Berechtigung fehlt.', 'error');
  }
  try {
    await window.App.storage.deliveryZones.restore(id);
    window.showToast && window.showToast('Zone wiederhergestellt.', 'success');
    _renderSettings();
  } catch (err) {
    console.error('[restoreDeliveryZone]', err);
    window.showToast && window.showToast('Fehler: ' + (err.message || err), 'error');
  }
}

/** Update eines Settings-Werts innerhalb des delivery-Sub-Objekts (S60 Block A).
 *  Permission-Gate: settings.rules.write (Lieferungs-Regeln leben im Regeln-Sub-Tab).
 *  Verwendet den globalen settings-saved-Indikator (Konsistenz mit updateSetting). */
export async function updateDeliveryToggle(key, val) {
  if (typeof window !== 'undefined' && window.App && window.App.auth
      && typeof window.App.auth.canEdit === 'function'
      && !window.App.auth.canEdit('settings.rules')) {
    if (typeof window.showToast === 'function') {
      window.showToast('Für diese Änderung fehlt dir die Berechtigung.', 'error');
    }
    return;
  }
  const state = getState();
  const cur = (state.settings && state.settings.delivery) || {};
  const next = { ...cur, [key]: val };
  state.settings.delivery = next;
  _lastSettingsSavedAt = new Date();
  const ind = document.getElementById('settings-saved');
  if (ind) {
    ind.textContent = 'Speichere...';
    ind.classList.add('flash');
  }
  try {
    await window.App.storage.settings.update({ delivery: next });
    if (ind) {
      ind.textContent = _getSettingsSavedHint();
      clearTimeout(ind._flashTimer);
      ind._flashTimer = setTimeout(() => ind.classList.remove('flash'), 600);
    }
  } catch (err) {
    console.error('[updateDeliveryToggle]', err);
    if (ind) { ind.textContent = 'Speichern fehlgeschlagen'; ind.classList.remove('flash'); }
    if (typeof window.showToast === 'function') {
      window.showToast('Speichern fehlgeschlagen: ' + (err.message || err), 'error');
    }
  }
}

/** S60 Block A2 — Liefer-Modus setzen (Single-Source). Mode ∈ {internal_only,
 *  internal_with_uber_fallback, uber_only}. Mappt zusätzlich auf die Legacy-
 *  Boolean-Felder, damit delivery-routing.js (S59) ohne Refactor weiterläuft. */
export async function updateDeliveryMode(mode) {
  if (!['internal_only','internal_with_uber_fallback','uber_only'].includes(mode)) {
    return;
  }
  if (typeof window !== 'undefined' && window.App && window.App.auth
      && typeof window.App.auth.canEdit === 'function'
      && !window.App.auth.canEdit('settings.rules')) {
    if (typeof window.showToast === 'function') {
      window.showToast('Für diese Änderung fehlt dir die Berechtigung.', 'error');
    }
    return;
  }
  const state = getState();
  const cur = (state.settings && state.settings.delivery) || {};
  // Mode → Legacy-Boolean-Mapping (Backward-Compat für delivery-routing.js)
  const flags = {
    internal_only:                  { internal_drivers_enabled: true,  uber_direct_fallback_enabled: false, uber_marketplace_enabled: false },
    internal_with_uber_fallback:    { internal_drivers_enabled: true,  uber_direct_fallback_enabled: true,  uber_marketplace_enabled: false },
    uber_only:                      { internal_drivers_enabled: false, uber_direct_fallback_enabled: false, uber_marketplace_enabled: true  }
  }[mode];
  // Uber-Connection-Stub (wird in S61 mit echten OAuth-Daten gefüllt)
  const uberConn = cur.uber_connection || { configured: false, account_id: null, last_sync: null, oauth_token_ref: null };
  const next = { ...cur, ...flags, mode, uber_connection: uberConn };
  state.settings.delivery = next;
  _lastSettingsSavedAt = new Date();
  const ind = document.getElementById('settings-saved');
  if (ind) {
    ind.textContent = 'Speichere...';
    ind.classList.add('flash');
  }
  try {
    await window.App.storage.settings.update({ delivery: next });
    if (ind) {
      ind.textContent = _getSettingsSavedHint();
      clearTimeout(ind._flashTimer);
      ind._flashTimer = setTimeout(() => ind.classList.remove('flash'), 600);
    }
    // UI-Re-Render damit der Uber-Warnhinweis korrekt unter dem aktiven Modus erscheint
    _renderSettings();
    // S63 — Empty-State-Warnung beim Wechsel auf internal_* ohne aktive Zonen.
    // Schickt nur einen Toast — die persistente Anzeige übernimmt der Banner in
    // renderDeliveryEmptyZonesWarning (async via loadDeliveryZonesList).
    if (mode !== 'uber_only' && window.App && window.App.storage && window.App.storage.deliveryZones) {
      try {
        const zones = await window.App.storage.deliveryZones.list();
        if (!zones || zones.length === 0) {
          window.showToast && window.showToast('⚠️ Keine Liefer-Zonen aktiv — bitte mindestens eine Zone anlegen, sonst werden alle Lieferanfragen abgewiesen.', 'warning', 6000);
        }
      } catch { /* nicht kritisch — Toast nur als Hinweis */ }
    }
  } catch (err) {
    console.error('[updateDeliveryMode]', err);
    if (ind) { ind.textContent = 'Speichern fehlgeschlagen'; ind.classList.remove('flash'); }
    if (typeof window.showToast === 'function') {
      window.showToast('Speichern fehlgeschlagen: ' + (err.message || err), 'error');
    }
  }
}

/** S60 Block B — Liefer-Zone via Adapter anlegen. */
export async function submitNewDeliveryZone() {
  if (typeof window !== 'undefined' && window.App && window.App.auth
      && typeof window.App.auth.canEdit === 'function'
      && !window.App.auth.canEdit('settings.rules')) {
    return window.showToast && window.showToast('Berechtigung fehlt.', 'error');
  }
  const name = (document.getElementById('zone-name') || {}).value || '';
  const plzRaw = (document.getElementById('zone-plz') || {}).value || '';
  const fee = Number((document.getElementById('zone-fee') || {}).value || 0);
  const min = Number((document.getElementById('zone-min') || {}).value || 0);
  if (!name.trim()) return window.showToast && window.showToast('Name eingeben.', 'error');
  // S60 W4.11 — Wildcard-Prefix erlauben: '8033*' für 80330-80339
  const plzList = plzRaw.split(',').map(s => s.trim()).filter(s => /^[0-9]{1,5}\*?$/.test(s));
  if (plzList.length === 0) return window.showToast && window.showToast('Mindestens eine PLZ (4-5 Ziffern, optional mit *-Suffix) eingeben.', 'error');
  try {
    await window.App.storage.deliveryZones.upsert({
      name: name.trim(),
      plzList,
      feeCents: Math.round(fee * 100),
      minOrderCents: Math.round(min * 100),
      active: true
    });
    window.showToast && window.showToast('Zone angelegt.', 'success');
    _renderSettings();
  } catch (err) {
    console.error('[submitNewDeliveryZone]', err);
    window.showToast && window.showToast('Fehler: ' + (err.message || err), 'error');
  }
}

/** S60 Block B — Zone soft-löschen.
 *  Konsistent mit menu_items/tables (S58): prompt() für optionalen Grund,
 *  hilft beim späteren Audit. Reason wird in deleted_reason persistiert. */
export async function deleteDeliveryZone(id) {
  if (!id) return;
  const _del = await window.styledConfirm({
    title: 'Zone löschen?', danger: true, confirmLabel: 'Löschen',
    body: 'Die Liefer-Zone wird soft-gelöscht (deleted_reason wird für das Audit gespeichert).',
    reasonField: true
  });
  if (!_del.confirmed) return;
  const reason = _del.reason || '';
  try {
    await window.App.storage.deliveryZones.softDelete(id, reason || null);
    window.showToast && window.showToast('Zone entfernt.', 'success');
    _renderSettings();
  } catch (err) {
    console.error('[deleteDeliveryZone]', err);
    window.showToast && window.showToast('Fehler: ' + (err.message || err), 'error');
  }
}

// -----------------------------------------------------------------------------
// Side-Effect: window.* für Inline-Handler im Settings-HTML (onclick/onchange).
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderDeliveryRulesCard,
    renderDeliveryEmptyZonesWarning,
    loadDeliveryZonesList,
    loadDeliveryZonesDeleted,
    loadUberConfigStatus,
    updateUberConfigField,
    testUberConnection,
    restoreDeliveryZone,
    updateDeliveryToggle,
    updateDeliveryMode,
    submitNewDeliveryZone,
    deleteDeliveryZone
  });
}
